import { BrokerInactiveNotice } from '@/features/brokers/components/broker-inactive-notice.component';
import { useSelectedBrokerWriteGuard } from '@/features/brokers/hooks';
import { NewQuoteForm } from '@/features/quotes/components/new-quote-form.component';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { QuoteSummaryProvider } from '@/features/quotes/context/quote-summary.context';
import type { BrokerProductCategory } from '@/services/brokers/types';
import {
  canCloneGroupLifeQuote,
  isTerminalPortalStatus,
  PortalStatus,
} from '@/shared/enums/portal-status.enum';
import { Page, PageHeader } from '@/shared/ui/page';
import { toaster } from '@/shared/ui/toaster.component';
import { isClonedQuoteMarkedAsDraft } from '@/shared/utils/clone-quote-status.util';
import { Button } from '@noorden-insurtech/alba-design-system';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Copy, Home } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useCopyQuote } from '../../hooks/common/use-copy-quote.hook';
import { useCopyGroupLifeQuote } from '../../hooks/group-life/use-copy-quote.hook';
import { useGroupLifeQuoteDetails } from '../../hooks/group-life/use-group-life-quote-details.hook';
import { GroupLifeClonePendingError } from '@/shared/utils/group-life-clone-idempotency.util';
import { useProposalDetails } from '../../hooks/homeowner/use-proposal-details.hook';
import { getInsuranceType } from '../../utils/getInsuranceType';
import type { QuoteWizardFlow } from '@/app/config/products.config';
import { CloneQuoteDialog } from '../clone-quote.dialog';

type Props = {
  productId: string;
  category: BrokerProductCategory;
  productCode: string;
  stepCode: string;
  quoteFlow: QuoteWizardFlow;
};

export function NewQuoteLayout(props: Props) {
  const { productId, category, productCode, stepCode, quoteFlow } = props;
  const { setParams, policyId, proposalNumber } = useQuoteParams();
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const [isOnLastStep, setIsOnLastStep] = useState(false);
  const [isCloneDialogOpen, setIsCloneDialogOpen] = useState(false);
  const copyQuoteMutation = useCopyQuote();
  const quoteWriteGuard = useSelectedBrokerWriteGuard('quote');
  const copyGroupLifeMutation = useCopyGroupLifeQuote();

  // Group Life não usa o endpoint comum `GET /portal/v1/quotes/:policyId`
  // (que resolve factory por productCode via `PolicyFactoryRegistry` —
  // Group Life não tem factory registrada lá; ele vive em
  // `policies/quotes/.../group-life/` com fluxo próprio CQRS). Os
  // detalhes da cotação ficam no contexto local `useGroupLifeQuote`.
  const isGroupLife = category === 'GROUP_LIFE_INSURANCE';
  const isEditFlow = quoteFlow === 'edit';
  const { data: proposalDetails } = useProposalDetails(
    isGroupLife ? undefined : policyId
  );
  const { data: groupLifeQuote } = useGroupLifeQuoteDetails(isGroupLife);

  useEffect(() => {
    setParams({
      productId,
      category,
      productCode,
      ...(params.id && {
        id: params.id as string,
        policyId: params.id as string,
      }),
    });
  }, [productId, category, productCode, params.id, setParams]);

  /** Nº da proposta (InsureMO) — após criar a cotação ou ao carregar os detalhes */
  useEffect(() => {
    const no = (
      proposalDetails?.quotationNo || proposalDetails?.proposalNo
    )?.trim();
    if (no && no !== proposalNumber) {
      setParams({ proposalNumber: no });
    }
  }, [
    proposalDetails?.quotationNo,
    proposalDetails?.proposalNo,
    proposalNumber,
    setParams,
  ]);

  const portalStatus =
    Number(
      isGroupLife ? groupLifeQuote?.statusPortal : proposalDetails?.statusPortal
    ) || 0;
  // Mesma pergunta do resumo e da trilha de steps: encerrada, expirada,
  // excluída ou recusada. Antes a lista estava repetida aqui.
  const isTerminalStatus = isTerminalPortalStatus(portalStatus);
  const resolvedPortalStatus =
    !isTerminalStatus && isClonedQuoteMarkedAsDraft(proposalDetails?.policyId)
      ? PortalStatus.QuoteInProgress
      : portalStatus;
  const canClone =
    resolvedPortalStatus === PortalStatus.QuoteInProgress ||
    resolvedPortalStatus === PortalStatus.QuoteCalculated;
  const showCloneAction =
    Boolean(policyId) &&
    (isGroupLife
      ? canCloneGroupLifeQuote(resolvedPortalStatus)
      : isOnLastStep && canClone);

  const proposalNoLabel =
    proposalNumber.trim() ||
    (proposalDetails?.quotationNo || proposalDetails?.proposalNo)?.trim() ||
    '';

  const isCloning =
    copyQuoteMutation.isPending || copyGroupLifeMutation.isPending;

  const handleCloseCloneDialog = () => {
    setIsCloneDialogOpen(false);
    copyQuoteMutation.reset();
    copyGroupLifeMutation.reset();
  };

  const handleConfirmClone = async () => {
    if (!policyId) {
      toaster.create({
        type: 'error',
        title: 'Não foi possível clonar',
        description: 'ID da cotação (PolicyId) não encontrado.',
      });
      handleCloseCloneDialog();
      return;
    }

    try {
      if (isGroupLife) {
        // Group Life tem endpoint de clonagem dedicado (o `/copy` comum não
        // suporta o produto — sem PolicyFactory 930016). Abre o clone no
        // wizard em modo edição; NÃO usamos `/quotes/$policyId/edit` porque
        // ele carrega detalhes pelo GET comum, que não funciona p/ Group Life.
        const { policyId: newPolicyId } =
          await copyGroupLifeMutation.mutateAsync(policyId);
        toaster.create({
          type: 'success',
          title: 'Cotação clonada com sucesso',
        });
        handleCloseCloneDialog();
        navigate({
          to: '/quotes/products/new/$id',
          params: { id: newPolicyId },
          search: {
            productId: productId || '',
            category: (category || '') as string,
            productCode: productCode || '',
            quoteFlow: 'edit',
            stepCode: '',
          },
        });
        return;
      }

      const { policyId: newPolicyId } = await copyQuoteMutation.mutateAsync({
        policyId,
        quoteNumber:
          proposalDetails?.quotationNo ||
          proposalDetails?.proposalNo ||
          proposalNumber,
        productCode: proposalDetails?.productCode || productCode,
      });

      toaster.create({
        type: 'success',
        title: 'Cotação clonada com sucesso',
      });

      handleCloseCloneDialog();
      navigate({
        to: '/quotes/$policyId/edit',
        params: { policyId: newPolicyId },
        search: { mode: 'edit' },
      });
    } catch (error) {
      // O clone pode ter sido concluído sem a resposta chegar: fecha o dialog
      // para o corretor conferir a lista em vez de confirmar de novo.
      if (error instanceof GroupLifeClonePendingError) {
        handleCloseCloneDialog();
        toaster.create({
          type: 'info',
          title: 'Clonar cotação',
          description: error.message,
        });
        return;
      }
      const message =
        error instanceof Error ? error.message : 'Erro ao clonar cotação.';

      toaster.create({
        type: 'error',
        title: 'Não foi possível clonar',
        description: message.includes('Deductible')
          ? `${message} (edite a cotação para ajustar franquias e tente clonar novamente)`
          : message,
      });
    }
  };

  return (
    <QuoteSummaryProvider>
      <Page.Root className='h-auto lg:h-full'>
        <Page.Breadcrumb
          items={[
            {
              label: 'Página Inicial',
              href: '/dashboard',
              iconLeft: <Home className='h-4 w-4' />,
            },
            { label: 'Cotações', href: '/quotes/products' },
            ...(proposalNoLabel
              ? [{ label: proposalNoLabel, active: true }]
              : [{ label: 'Cotação', active: true }]),
          ]}
        />

        <PageHeader.Root>
          <PageHeader.Main>
            <PageHeader.TitleGroup>
              <PageHeader.Title>
                {/* Cotação encerrada não é "Nova" nem está sendo editada: o
                    que a tela mostra é a cotação, e só. */}
                {isTerminalStatus
                  ? `Cotação - ${getInsuranceType(category)}`
                  : isEditFlow
                    ? `Editar Cotação - ${getInsuranceType(category)}`
                    : `Nova Cotação - ${getInsuranceType(category)}`}
              </PageHeader.Title>
              <PageHeader.Subtitle>
                {isTerminalStatus
                  ? 'Visualize os detalhes da cotação'
                  : isEditFlow
                    ? 'Edite as informações da cotação'
                    : 'Preencha as informações para calcular o seguro'}
              </PageHeader.Subtitle>
            </PageHeader.TitleGroup>
            {showCloneAction && (
              <PageHeader.Actions>
                <div
                  title={quoteWriteGuard.reason}
                  className='flex flex-col items-end gap-1'
                >
                  <Button
                    variant='ghost'
                    size='large'
                    loading={isCloning}
                    disabled={isCloning || quoteWriteGuard.blocked}
                    onClick={() => setIsCloneDialogOpen(true)}
                  >
                    <Copy className='h-5 w-5' strokeWidth={1.6} />
                    Clonar
                  </Button>
                </div>
              </PageHeader.Actions>
            )}
          </PageHeader.Main>
        </PageHeader.Root>

        <BrokerInactiveNotice guards={[quoteWriteGuard]} />

        <Page.Content className='py-4'>
          <NewQuoteForm
            stepCodeFromSearch={stepCode}
            onLastStepChange={setIsOnLastStep}
          />
        </Page.Content>

        <CloneQuoteDialog
          isOpen={isCloneDialogOpen}
          isLoading={isCloning}
          onClose={handleCloseCloneDialog}
          onConfirm={handleConfirmClone}
        />
      </Page.Root>
    </QuoteSummaryProvider>
  );
}
